import { Fragment, type ReactNode } from 'react'

/*
 * 답장 · 도입부 글의 작은 서식 — 모델과 작가가 실제로 쓰는 세 가지만 읽는다. HTML 은 만들지 않는다(React 요소만).
 *  - ``` 로 감싼 칸 → 상태창 칸. 글자 폭이 같은 글꼴에 줄 · 칸을 그대로 둬서 작가가 맞춘 정렬이 무너지지 않는다
 *  - *행동* → 별표를 감추고 기울인 옅은 글씨(소설형 도입부에 별표가 그대로 보이던 문제)
 *  - **강조** → 굵게, `짧은 값` → 작은 값 칸
 * 닫히지 않은 ``` 는 끝까지 상태창으로 본다 — 모델이 닫는 줄을 빠뜨려도 뒤 글이 깨진 기호로 보이지 않는다.
 */

const FENCE = /```[^\n]*\n?([\s\S]*?)(?:```|$)/g
const INLINE = /(\*\*[^*\n]+\*\*|\*[^*\n]+\*|`[^`\n]+`)/g

function inline(text: string, keyBase: string): ReactNode[] {
  return text.split(INLINE).map((part, i) => {
    const key = `${keyBase}-${i}`
    if (part.length > 4 && part.startsWith('**') && part.endsWith('**'))
      return (
        <strong key={key} className="font-bold">
          {part.slice(2, -2)}
        </strong>
      )
    if (part.length > 2 && part.startsWith('*') && part.endsWith('*'))
      return (
        <em key={key} className="italic opacity-70">
          {part.slice(1, -1)}
        </em>
      )
    if (part.length > 2 && part.startsWith('`') && part.endsWith('`'))
      return (
        <code
          key={key}
          className="rounded-[4px] bg-foreground/[0.08] px-1 py-px font-mono text-[0.9em] not-italic"
        >
          {part.slice(1, -1)}
        </code>
      )
    return <Fragment key={key}>{part}</Fragment>
  })
}

/** 서식 기호가 없는 글은 문자열 그대로 돌려준다 — 대부분의 줄이 여기로 빠져 추가 요소가 생기지 않는다 */
export function RichText({ text }: { text: string }) {
  if (!/[*`]/.test(text)) return <>{text}</>
  const out: ReactNode[] = []
  let last = 0
  for (const match of text.matchAll(FENCE)) {
    const at = match.index ?? 0
    if (at > last)
      out.push(...inline(text.slice(last, at).replace(/\n$/, ''), `t${at}`))
    const body = match[1].replace(/\n$/, '')
    if (body.trim()) {
      out.push(
        <span
          key={`f${at}`}
          role="group"
          aria-label="상태창"
          className="my-1.5 block overflow-x-auto rounded-field bg-black/40 px-3 py-2.5 text-left font-mono text-c12 leading-[1.7] whitespace-pre text-t2 not-italic shadow-[inset_0_0_0_1px_rgb(255_255_255/0.08)] scrollbar-none"
        >
          {body}
        </span>
      )
    }
    last = at + match[0].length
  }
  if (last < text.length)
    out.push(...inline(text.slice(last).replace(/^\n/, ''), `t${last}`))
  return <>{out}</>
}
